/**
 * informesDelDueno -- los tres informes que el dueno pide en Excel (INF-T4, mision INFORMES-DUENO-V1-001; Ares #40159).
 *
 * Contrato: nodo ep-informes-dueno de nexo-api (PR 716 de A, InformesDuenoController):
 *   GET /api/v1/businesses/{id}/informes/egresos-por-concepto?desde&hasta&conceptos=a,b&metodo=Caja|Externo
 *   GET /api/v1/businesses/{id}/informes/pagos-proveedor?desde&hasta&concepto=texto (proveedorId da 422: egresos no
 *       guarda el proveedor; contrato v2 del nodo, Q #40165)
 *   GET /api/v1/businesses/{id}/informes/financiero-mensual?desde&hasta
 * Egresos y proveedor responden {desde, hasta, filtros, resumen[{concepto,caja,externo,total}], porMes[{mes,total}],
 * detalle[{fecha,concepto,total,metodo}], total}; el financiero {desde, hasta, meses[{mes,ingresos,egresos,utilidad,pct}],
 * total{mes:'TOTAL',...}, promedioUtilidad}. El metodo del detalle es Caja, Externo o "Sin metodo".
 *
 * Aqui viven las reglas y las HOJAS del Excel (celdas con tipo); InformesView pinta y exportarExcel.js escribe el archivo.
 * Sin DOM y sin red.
 *
 * @module application/informes/informesDelDueno
 */

/** Las tres pestanas, en el orden de la vista. */
export const INFORMES = [
  { clave: 'egresos', nombre: 'Egresos por concepto', ruta: 'egresos-por-concepto' },
  { clave: 'proveedor', nombre: 'Pagos a un proveedor', ruta: 'pagos-proveedor' },
  { clave: 'financiero', nombre: 'Financiero mensual', ruta: 'financiero-mensual' },
];

/** El tope de la API (InformesDuenoService.MesesMaximos): un rango de 24 meses o mas responde 400. */
export const MESES_MAXIMOS = 24;

const n = (v) => (v === null || v === undefined || v === '' || Number.isNaN(Number(v)) ? 0 : Number(v));
const s = (v) => (v === null || v === undefined ? '' : String(v));
const lista = (v) => (Array.isArray(v) ? v : []);
const RE_DIA = /^(\d{4})-(\d{2})-(\d{2})$/;
const RE_MES = /^(\d{4})-(\d{2})$/;

/**
 * El rango por defecto: los ultimos 12 meses contando el actual (del dia 1 de hace 11 meses a hoy). Josse pidio TODOS
 * los egresos por defecto y la API topa en 24 meses; 12 es el ano completo que el dueno compara.
 * hoy = 'yyyy-MM-dd'. -> {desde, hasta}
 */
export function rangoPorDefecto(hoy) {
  const m = RE_DIA.exec(s(hoy));
  if (!m) return { desde: '', hasta: '' };
  const total = Number(m[1]) * 12 + (Number(m[2]) - 1) - 11;
  const anio = Math.floor(total / 12);
  const mes = String((total % 12) + 1).padStart(2, '0');
  return { desde: `${anio}-${mes}-01`, hasta: hoy };
}

/** El error de las fechas con la misma regla de la API (ErrorDeFechas), para no gastar el viaje; null si sirven. */
export function errorDeFechas(desde, hasta) {
  const a = RE_DIA.exec(s(desde)); const b = RE_DIA.exec(s(hasta));
  if (!a || !b) return 'Elige las dos fechas, desde y hasta.';
  if (s(hasta) < s(desde)) return 'La fecha final no puede ser antes que la inicial.';
  // desde.AddMonths(24) <= hasta es error en la API: mismo dia del mes 24 meses despues, y como .NET, sin pasarse del
  // ultimo dia de ese mes (29-feb + 24 meses = 28-feb).
  const anio = Number(a[1]) + 2;
  const ultimo = new Date(Date.UTC(anio, Number(a[2]), 0)).getUTCDate();
  const limite = `${anio}-${a[2]}-${String(Math.min(Number(a[3]), ultimo)).padStart(2, '0')}`;
  if (s(hasta) >= limite) return `El rango no puede pasar de ${MESES_MAXIMOS} meses.`;
  return null;
}

/**
 * La consulta de cada informe, en el orden en que la API la lee. filtros: {desde, hasta, conceptos[], metodo,
 * proveedorId, concepto}. Lo vacio no se manda (un conceptos= vacio filtraria por nada).
 */
export function consultaDe(clave, filtros = {}) {
  const q = new URLSearchParams();
  q.set('desde', s(filtros.desde));
  q.set('hasta', s(filtros.hasta));
  if (clave === 'egresos') {
    const c = lista(filtros.conceptos).map((x) => s(x).trim()).filter(Boolean);
    if (c.length) q.set('conceptos', c.join(','));
    if (filtros.metodo) q.set('metodo', s(filtros.metodo));
  } else if (clave === 'proveedor') {
    // Solo por concepto, como la 1.0: proveedorId responde 422 porque egresos no guarda el proveedor (Q #40162/#40165).
    if (s(filtros.concepto).trim()) q.set('concepto', s(filtros.concepto).trim());
    if (filtros.metodo) q.set('metodo', s(filtros.metodo));
  }
  return q.toString();
}

/** Pagos a un proveedor pide el concepto (la API responde 400 sin el). null si esta bien. */
export function faltaProveedor(filtros = {}) {
  return s(filtros.concepto).trim() ? null : 'Escribe el concepto con que se registran los pagos a ese proveedor, tal como en la caja.';
}

/** Lee la respuesta de egresos o de proveedor, en camel o en Pascal. */
export function leerEgresos(d) {
  const x = d && typeof d === 'object' ? d : {};
  const total = n(x.total ?? x.Total);
  const resumen = lista(x.resumen ?? x.Resumen).map((r) => {
    const t = n(r.total ?? r.Total);
    const caja = n(r.caja ?? r.Caja); const externo = n(r.externo ?? r.Externo);
    // La API no manda "Sin metodo" en el resumen; sin esa columna Caja + Externo no da el Total y se lee como error.
    return { concepto: s(r.concepto ?? r.Concepto), caja, externo, sinMetodo: Math.max(0, t - caja - externo), total: t,
      pct: total ? Math.round((1000 * t) / total) / 10 : 0 };
  });
  return {
    desde: s(x.desde ?? x.Desde), hasta: s(x.hasta ?? x.Hasta), total, resumen,
    porMes: lista(x.porMes ?? x.PorMes).map((m) => ({ mes: s(m.mes ?? m.Mes), total: n(m.total ?? m.Total) })),
    detalle: lista(x.detalle ?? x.Detalle).map((f) => ({ fecha: s(f.fecha ?? f.Fecha).slice(0, 10), concepto: s(f.concepto ?? f.Concepto),
      metodo: s(f.metodo ?? f.Metodo), total: n(f.total ?? f.Total) })),
  };
}

/** Lee la respuesta del financiero mensual, en camel o en Pascal. */
export function leerFinanciero(d) {
  const x = d && typeof d === 'object' ? d : {};
  const mes = (m) => ({ mes: s(m?.mes ?? m?.Mes), ingresos: n(m?.ingresos ?? m?.Ingresos), egresos: n(m?.egresos ?? m?.Egresos),
    utilidad: n(m?.utilidad ?? m?.Utilidad), pct: n(m?.pct ?? m?.Pct) });
  return { desde: s(x.desde ?? x.Desde), hasta: s(x.hasta ?? x.Hasta), meses: lista(x.meses ?? x.Meses).map(mes),
    total: mes(x.total ?? x.Total ?? {}), promedioUtilidad: n(x.promedioUtilidad ?? x.PromedioUtilidad) };
}

/**
 * Las columnas del resumen por concepto, iguales en pantalla y en el Excel. "Sin metodo" sale solo si algun concepto
 * tiene egresos sin Caja ni Externo. -> [{clave, titulo}]
 */
export function columnasDelResumen(e) {
  const sin = lista(e?.resumen).some((r) => r.sinMetodo);
  return [{ clave: 'concepto', titulo: 'Concepto' }, { clave: 'caja', titulo: 'Caja' }, { clave: 'externo', titulo: 'Externo' },
    ...(sin ? [{ clave: 'sinMetodo', titulo: 'Sin método' }] : []), { clave: 'total', titulo: 'Total' }, { clave: 'pct', titulo: '% del total' }];
}

/** La fila TOTAL del resumen en el orden de columnasDelResumen: 'TOTAL', las sumas y 100 (o 0 sin egresos). */
export function filaTotalDelResumen(e) {
  return columnasDelResumen(e).map((c) => (c.clave === 'concepto' ? 'TOTAL' : c.clave === 'pct' ? (e.total ? 100 : 0)
    : c.clave === 'total' ? e.total : lista(e.resumen).reduce((a, r) => a + n(r[c.clave]), 0)));
}

/** Un informe sin filas: lo dice con el rango, para que un vacio no se lea como herramienta rota. */
export function estaVacio(clave, inf) {
  return clave === 'financiero' ? !lista(inf?.meses).length : !lista(inf?.detalle).length;
}

/** Pesos sin decimales con separador de Colombia. */
export const pesos = (v) => '$ ' + n(v).toLocaleString('es-CO', { maximumFractionDigits: 0 });

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
/** 'yyyy-MM' -> 'sep 2026' (lo que lee el dueno); lo que no tenga esa forma pasa igual. */
export function nombreDelMes(mes) {
  const m = RE_MES.exec(s(mes));
  return m ? `${MESES[Number(m[2]) - 1]} ${m[1]}` : s(mes);
}

/**
 * La fecha como numero de serie de Excel (dias desde 1899-12-30), calculada en UTC. Con new Date(...) local la prueba
 * en Bogota corrio un dia: 2026-09-03 salio 2026-09-02. 'yyyy-MM' cuenta como el dia 1 del mes. null si no es fecha.
 */
export function serialDeExcel(fecha) {
  const t = s(fecha);
  const m = RE_DIA.exec(t) || (RE_MES.test(t) ? RE_DIA.exec(t + '-01') : null);
  if (!m) return null;
  return Math.round((Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) - Date.UTC(1899, 11, 30)) / 86400000);
}

// Celdas con tipo para el Excel: montos como numero, fechas como fecha (serial + formato), textos como texto.
const FORMATO_PESOS = '#,##0';
const texto = (v) => ({ t: 's', v: s(v) });
const plata = (v) => ({ t: 'n', v: n(v), z: FORMATO_PESOS });
const dia = (v) => { const x = serialDeExcel(v); return x === null ? texto(v) : { t: 'n', v: x, z: 'yyyy-mm-dd' }; };
const mesCelda = (v) => { const x = serialDeExcel(v); return x === null ? texto(v) : { t: 'n', v: x, z: 'yyyy-mm' }; };
const porcentaje = (v) => ({ t: 'n', v: n(v) / 100, z: '0%' });
const fraccion = (v) => ({ t: 'n', v: n(v) / 100, z: '0.0%' });

/**
 * Las hojas del Excel de un informe ya leido. -> [{nombre, filas: [[celda]], anchos: [n]}]
 *   egresos y proveedor: Resumen (Concepto, Caja, Externo, Total, % del total, con fila TOTAL), Por mes, Detalle;
 *   financiero: Por mes (Mes, Ingresos, Egresos, Utilidad, %), con la fila TOTAL y el promedio de la utilidad.
 */
export function hojasDelInforme(clave, inf) {
  if (clave === 'financiero') {
    const f = inf || leerFinanciero(null);
    const filas = [['Mes', 'Ingresos', 'Egresos', 'Utilidad', '%'].map(texto),
      ...f.meses.map((m) => [mesCelda(m.mes), plata(m.ingresos), plata(m.egresos), plata(m.utilidad), porcentaje(m.pct)]),
      [texto('TOTAL'), plata(f.total.ingresos), plata(f.total.egresos), plata(f.total.utilidad), porcentaje(f.total.pct)],
      [texto('Promedio de utilidad por mes'), texto(''), texto(''), plata(f.promedioUtilidad), texto('')]];
    return [{ nombre: 'Por mes', filas, anchos: [28, 16, 16, 16, 8] }];
  }
  const e = inf || leerEgresos(null);
  return [
    { nombre: 'Resumen', anchos: columnasDelResumen(e).map((c) => (c.clave === 'concepto' ? 36 : c.clave === 'pct' ? 12 : 16)),
      filas: [columnasDelResumen(e).map((c) => texto(c.titulo)),
        ...e.resumen.map((r) => columnasDelResumen(e).map((c) => (c.clave === 'concepto' ? texto(r.concepto) : c.clave === 'pct' ? fraccion(r.pct) : plata(r[c.clave])))),
        filaTotalDelResumen(e).map((v, i) => (i === 0 ? texto(v) : i === columnasDelResumen(e).length - 1 ? fraccion(v) : plata(v)))] },
    { nombre: 'Por mes', anchos: [12, 16], filas: [['Mes', 'Total'].map(texto), ...e.porMes.map((m) => [mesCelda(m.mes), plata(m.total)]),
      [texto('TOTAL'), plata(e.total)]] },
    { nombre: 'Detalle', anchos: [12, 36, 12, 16], filas: [['Fecha', 'Concepto', 'Metodo', 'Total'].map(texto),
      ...e.detalle.map((f) => [dia(f.fecha), texto(f.concepto), texto(f.metodo), plata(f.total)])] },
  ];
}

/** El nombre del archivo: informe, negocio y rango, sin caracteres que Windows no acepta. */
export function nombreDelArchivo(clave, negocio, desde, hasta) {
  const limpio = (v) => s(v).normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase();
  const inf = INFORMES.find((i) => i.clave === clave);
  return ['informe', limpio(inf ? inf.ruta : clave), limpio(negocio) || 'negocio', `${s(desde)}_${s(hasta)}`].join('-') + '.xlsx';
}

/** Por que no hay informe, por causa y por ambiente (F3: un mensaje unico hace dudar de lo que si funciona). */
export function porQueNoHay(motivo, env = 'prod', detalle = '') {
  if (env === 'dev' && (motivo === 'NO_PUBLICADO' || motivo === 'SIN_RED')) {
    return 'Estás en dev: el panel le pregunta a la API local. En producción los informes son los de nexo-api 716.';
  }
  switch (motivo) {
    case 'SIN_NEGOCIO': return 'Elige un negocio en la barra de arriba para ver sus informes.';
    case 'NO_PUBLICADO': return 'Falta publicar los informes en la API (nexo-api 716). Cuando salga, esta vista se enciende sola.';
    case 'PROHIBIDO': return 'Los informes de plata son solo para el dueño o el administrador del negocio.';
    case 'DATOS': return detalle || 'Faltan datos o alguno no tiene el formato que la API espera.';
    case 'SIN_RED': return 'No pudimos hablar con la API ahora. Vuelve a intentar en un momento.';
    default: return detalle || 'La API no pudo armar el informe ahora.';
  }
}
